/**
 * Custom VIDEO player (F3-F): tailored controls over a plain `<video>`.
 *
 * Signed playback (presigned URL with TTL) + local-first resume: on mount
 * reads `video_watch_progress` (PowerSync) and jumps to the saved point;
 * every 5s / on pause / on unmount does `upsertWatchProgress` with a
 * MONOTONIC `watched_pct` (`WatchTracker`'s anti-skip heuristic). If the
 * `<video>` fails due to an expired URL, it re-signs transparently and
 * restores the position. Offline it shows a card with retry (videos aren't
 * cached offline in v1). Controls: play/pause, bar with buffer, times,
 * speed, volume, fullscreen, PiP, keyboard. `playsinline`, reduced-motion
 * safe, no scroll-x.
 */
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  Maximize,
  Minimize,
  Pause,
  PictureInPicture,
  Play,
  RefreshCw,
  Volume2,
  VolumeX,
  WifiOff,
} from "@fleet/icons";
import {
  clampPct,
  computeResumeSeek,
  createPlayUrlController,
  createWatchTrackerState,
  DEFAULT_MIN_WATCH_PCT,
  formatTime,
  isWatchComplete,
  setTrackerDuration,
  trackPosition,
  upsertWatchProgress,
  watchedFraction,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useCourseWrite } from "@/features/courses/hooks/use-course-data";
import { mediaApi, type MediaApi } from "../lib/media-api";
import { useWatchProgress } from "../hooks/use-media";

export interface VideoPlayerProps {
  /** Id of the `video_asset` to play. */
  videoId: string;
  /** Accessible title of the video. */
  title?: string;
  /** Known duration (metadata) for the track before the `<video>` loads. */
  durationSeconds?: number | null;
  /** Completion threshold (to emit `onCompleted`). Default 0.9. */
  minWatchPct?: number;
  /** Notifies the (monotonic) `watched_pct` and whether it crossed the threshold. */
  onWatchedPctChange?: (watchedPct: number, completed: boolean) => void;
  /** Media API transport (injectable in tests). */
  api?: MediaApi;
}

/** Progress persistence interval during playback. */
const PERSIST_INTERVAL_MS = 5000;
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const SEEK_STEP_SECONDS = 5;
const VOLUME_STEP = 0.1;

export const VideoPlayer: Component<VideoPlayerProps> = (props) => {
  const { t } = useI18n();
  const { drizzle, userId } = useCourseWrite();
  const api = () => props.api ?? mediaApi;
  const minWatch = () => props.minWatchPct ?? DEFAULT_MIN_WATCH_PCT;

  const watch = useWatchProgress(() => props.videoId);

  // Setup reads (once only): the video is fixed for as long as the player
  // lives (it's keyed by `videoId`), so they're read without reactive tracking.
  const initialVideoId = untrack(() => props.videoId);
  const initialDuration = untrack(() => props.durationSeconds ?? 0);

  let videoEl: HTMLVideoElement | undefined;
  const controller = createPlayUrlController(initialVideoId, (id) => api().getPlayUrl(id));
  const tracker = createWatchTrackerState(initialDuration);

  const [playing, setPlaying] = createSignal(false);
  const [current, setCurrent] = createSignal(0);
  const [duration, setDuration] = createSignal(initialDuration);
  const [buffered, setBuffered] = createSignal(0);
  const [volume, setVolume] = createSignal(1);
  const [muted, setMuted] = createSignal(false);
  const [rate, setRate] = createSignal(1);
  const [fullscreen, setFullscreen] = createSignal(false);
  const [speedOpen, setSpeedOpen] = createSignal(false);
  const [offline, setOffline] = createSignal(false);
  const [loading, setLoading] = createSignal(true);
  const [resumed, setResumed] = createSignal(false);

  let container: HTMLDivElement | undefined;
  let lastPersist = 0;
  let seededFromProgress = false;

  const pipSupported = createMemo(
    () => typeof document !== "undefined" && "pictureInPictureEnabled" in document,
  );

  const posterUrl = createMemo(() => api().posterUrl(props.videoId));
  const pct = () => {
    const d = duration();
    return d > 0 ? clampPct(current() / d) : 0;
  };

  // ── Signed source loading ──────────────────────────────────────────────
  async function loadSource(restorePosition?: number): Promise<void> {
    if (!videoEl) {
      return;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setOffline(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = await controller.get();
      setOffline(false);
      videoEl.src = url;
      if (restorePosition !== undefined && Number.isFinite(restorePosition)) {
        videoEl.currentTime = restorePosition;
      }
      videoEl.load();
    } catch {
      // Network failure while signing → treated as offline (retryable).
      setOffline(true);
      setLoading(false);
    }
  }

  onMount(() => {
    void loadSource();
    const onlineHandler = () => {
      if (offline()) {
        void loadSource(current());
      }
    };
    const offlineHandler = () => setOffline(true);
    const fsHandler = () =>
      setFullscreen(
        Boolean(document.fullscreenElement) && document.fullscreenElement === container,
      );
    globalThis.addEventListener?.("online", onlineHandler);
    globalThis.addEventListener?.("offline", offlineHandler);
    document.addEventListener?.("fullscreenchange", fsHandler);
    onCleanup(() => {
      globalThis.removeEventListener?.("online", onlineHandler);
      globalThis.removeEventListener?.("offline", offlineHandler);
      document.removeEventListener?.("fullscreenchange", fsHandler);
      void persist(true);
    });
  });

  // Seeds the tracker with prior progress once the local row arrives.
  createEffect(() => {
    const row = watch.data;
    if (row && !seededFromProgress && duration() > 0) {
      seededFromProgress = true;
      const seeded = Math.round(clampPct(row.watched_pct ?? 0) * Math.ceil(duration()));
      for (let i = 0; i < seeded; i++) {
        tracker.covered.add(i);
      }
    }
  });

  // ── Progress persistence ───────────────────────────────────────────────
  async function persist(force = false): Promise<void> {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid || !videoEl) {
      return;
    }
    const now = Date.now();
    if (!force && now - lastPersist < PERSIST_INTERVAL_MS) {
      return;
    }
    lastPersist = now;
    const watchedPct = watchedFraction(tracker);
    const completed = isWatchComplete(watchedPct, minWatch());
    try {
      await upsertWatchProgress(db, uid, props.videoId, {
        positionSeconds: videoEl.currentTime,
        watchedPct,
        completed,
      });
    } catch {
      /* offline / no permission: the next tick retries */
    }
    props.onWatchedPctChange?.(watchedPct, completed);
  }

  // ── <video> handlers ────────────────────────────────────────────────────
  const onLoadedMetadata = () => {
    if (!videoEl) {
      return;
    }
    const d = Number.isFinite(videoEl.duration) ? videoEl.duration : (props.durationSeconds ?? 0);
    setDuration(d);
    setTrackerDuration(tracker, d);
    setLoading(false);
    if (!resumed()) {
      setResumed(true);
      const seek = computeResumeSeek({
        positionSeconds: watch.data?.position_seconds ?? null,
        durationSeconds: d,
        completed: Boolean(watch.data?.completed_at),
      });
      if (seek !== null) {
        videoEl.currentTime = seek;
        setCurrent(seek);
      }
    }
  };

  const onTimeUpdate = () => {
    if (!videoEl) {
      return;
    }
    setCurrent(videoEl.currentTime);
    trackPosition(tracker, videoEl.currentTime);
    if (videoEl.buffered.length > 0) {
      setBuffered(videoEl.buffered.end(videoEl.buffered.length - 1));
    }
    void persist();
  };

  const onError = () => {
    if (!videoEl) {
      return;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setOffline(true);
      return;
    }
    // The signed URL may have expired → re-sign and restore position.
    const el = videoEl;
    const position = el.currentTime || current();
    void (async () => {
      try {
        const url = await controller.refresh();
        el.src = url;
        el.currentTime = position;
        el.load();
        if (playing()) {
          await el.play().catch(() => {});
        }
      } catch {
        setOffline(true);
      }
    })();
  };

  // ── Controls ───────────────────────────────────────────────────────────────
  const togglePlay = async () => {
    if (!videoEl || offline()) {
      return;
    }
    if (videoEl.paused) {
      await videoEl.play().catch(() => {});
    } else {
      videoEl.pause();
    }
  };

  const seekTo = (time: number) => {
    if (!videoEl) {
      return;
    }
    const clamped = Math.max(0, Math.min(time, duration() || videoEl.duration || 0));
    videoEl.currentTime = clamped;
    setCurrent(clamped);
  };

  const onSeekInput = (e: InputEvent & { currentTarget: HTMLInputElement }) => {
    const frac = Number(e.currentTarget.value) / 1000;
    seekTo(frac * (duration() || 0));
  };

  const changeVolume = (v: number) => {
    if (!videoEl) {
      return;
    }
    const clamped = Math.max(0, Math.min(1, v));
    videoEl.volume = clamped;
    setVolume(clamped);
    if (clamped > 0 && muted()) {
      videoEl.muted = false;
      setMuted(false);
    }
  };

  const toggleMute = () => {
    if (!videoEl) {
      return;
    }
    videoEl.muted = !videoEl.muted;
    setMuted(videoEl.muted);
  };

  const setSpeed = (r: number) => {
    if (videoEl) {
      videoEl.playbackRate = r;
    }
    setRate(r);
    setSpeedOpen(false);
  };

  const toggleFullscreen = async () => {
    if (!container) {
      return;
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {});
    } else {
      await container.requestFullscreen?.().catch(() => {});
    }
  };

  const togglePip = async () => {
    if (!videoEl || !pipSupported()) {
      return;
    }
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await videoEl.requestPictureInPicture();
      }
    } catch {
      /* PiP not available in this context */
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case " ":
      case "k": {
        e.preventDefault();
        void togglePlay();
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        seekTo(current() - SEEK_STEP_SECONDS);
        break;
      }
      case "ArrowRight": {
        e.preventDefault();
        seekTo(current() + SEEK_STEP_SECONDS);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        changeVolume(volume() + VOLUME_STEP);
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        changeVolume(volume() - VOLUME_STEP);
        break;
      }
      case "f": {
        e.preventDefault();
        void toggleFullscreen();
        break;
      }
      case "m": {
        e.preventDefault();
        toggleMute();
        break;
      }
      default: {
        break;
      }
    }
  };

  const iconBtn =
    "flex h-9 w-9 items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60";

  return (
    <div
      ref={container}
      class="relative w-full overflow-hidden rounded-2xl bg-black"
      data-testid="video-player"
      tabindex="0"
      role="region"
      aria-label={props.title ?? t(msg`Video player`)}
      onKeyDown={onKeyDown}
    >
      <Show
        when={!offline()}
        fallback={<OfflineCard poster={posterUrl()} onRetry={() => void loadSource(current())} />}
      >
        <div class="relative aspect-video w-full">
          {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- author's VOD; captions are v2 */}
          <video
            ref={videoEl}
            class="h-full w-full bg-black"
            data-testid="video-element"
            playsinline
            poster={posterUrl()}
            preload="metadata"
            onLoadedMetadata={onLoadedMetadata}
            onTimeUpdate={onTimeUpdate}
            onPlay={() => setPlaying(true)}
            onPause={() => {
              setPlaying(false);
              void persist(true);
            }}
            onEnded={() => {
              setPlaying(false);
              void persist(true);
            }}
            onError={onError}
            onClick={() => void togglePlay()}
            onVolumeChange={() => {
              if (videoEl) {
                setVolume(videoEl.volume);
                setMuted(videoEl.muted);
              }
            }}
          />
          <Show when={loading()}>
            <div
              class="pointer-events-none absolute inset-0 flex items-center justify-center"
              data-testid="video-loading"
            >
              <span class="motion-safe:animate-spin">
                <RefreshCw class="h-8 w-8 text-white/70" />
              </span>
            </div>
          </Show>
        </div>

        {/* Controls */}
        <div class="absolute inset-x-0 bottom-0 space-y-1 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6">
          {/* Progress bar with buffer */}
          <div class="relative flex items-center">
            <div class="pointer-events-none absolute inset-x-0 h-1.5 overflow-hidden rounded-full bg-white/25">
              <div
                class="absolute inset-y-0 left-0 bg-white/30"
                style={{ width: `${duration() ? (buffered() / duration()) * 100 : 0}%` }}
              />
              <div
                class="absolute inset-y-0 left-0 bg-accent"
                style={{ width: `${pct() * 100}%` }}
              />
            </div>
            <input
              type="range"
              min="0"
              max="1000"
              step="1"
              value={Math.round(pct() * 1000)}
              onInput={onSeekInput}
              data-testid="video-seek"
              aria-label={t(msg`Seek`)}
              class="relative z-10 h-1.5 w-full cursor-pointer appearance-none bg-transparent accent-accent"
            />
          </div>

          <div class="flex items-center gap-1 text-white">
            <button
              type="button"
              class={iconBtn}
              data-testid="video-play-toggle"
              aria-label={playing() ? t(msg`Pause`) : t(msg`Play`)}
              onClick={() => void togglePlay()}
            >
              <Show when={playing()} fallback={<Play class="h-5 w-5" />}>
                <Pause class="h-5 w-5" />
              </Show>
            </button>

            <button
              type="button"
              class={iconBtn}
              data-testid="video-mute"
              aria-label={muted() ? t(msg`Unmute`) : t(msg`Mute`)}
              onClick={toggleMute}
            >
              <Show when={muted() || volume() === 0} fallback={<Volume2 class="h-5 w-5" />}>
                <VolumeX class="h-5 w-5" />
              </Show>
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={muted() ? 0 : volume()}
              onInput={(e) => changeVolume(Number(e.currentTarget.value))}
              data-testid="video-volume"
              aria-label={t(msg`Volume`)}
              class="hidden h-1 w-16 cursor-pointer appearance-none rounded-full bg-white/30 accent-white sm:block"
            />

            <span class="ml-1 select-none text-xs tabular-nums text-white/90">
              <span data-testid="video-current-time">{formatTime(current())}</span>
              {" / "}
              <span data-testid="video-duration">{formatTime(duration())}</span>
            </span>

            <div class="ml-auto flex items-center gap-1">
              {/* Speed */}
              <div class="relative">
                <button
                  type="button"
                  class="flex h-9 min-w-[2.75rem] items-center justify-center rounded-lg px-2 text-xs font-bold text-white/90 transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  data-testid="video-speed"
                  aria-haspopup="menu"
                  aria-expanded={speedOpen()}
                  aria-label={t(msg`Playback speed`)}
                  onClick={() => setSpeedOpen((v) => !v)}
                >
                  {rate()}×
                </button>
                <Show when={speedOpen()}>
                  <div
                    role="menu"
                    class="absolute bottom-11 right-0 z-20 min-w-[4rem] overflow-hidden rounded-lg bg-neutral-900/95 py-1 text-sm text-white shadow-xl ring-1 ring-white/10"
                    data-testid="video-speed-menu"
                  >
                    <For each={SPEEDS}>
                      {(s) => (
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={rate() === s}
                          class="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-white/15"
                          classList={{ "font-bold text-accent": rate() === s }}
                          data-testid={`video-speed-${s}`}
                          onClick={() => setSpeed(s)}
                        >
                          {s}×
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              <Show when={pipSupported()}>
                <button
                  type="button"
                  class={iconBtn}
                  data-testid="video-pip"
                  aria-label={t(msg`Picture in picture`)}
                  onClick={() => void togglePip()}
                >
                  <PictureInPicture class="h-5 w-5" />
                </button>
              </Show>

              <button
                type="button"
                class={iconBtn}
                data-testid="video-fullscreen"
                aria-label={fullscreen() ? t(msg`Exit fullscreen`) : t(msg`Fullscreen`)}
                onClick={() => void toggleFullscreen()}
              >
                <Show when={fullscreen()} fallback={<Maximize class="h-5 w-5" />}>
                  <Minimize class="h-5 w-5" />
                </Show>
              </button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

/** "No connection" card: the video needs a network (not cached offline in v1). */
const OfflineCard: Component<{ poster: string; onRetry: () => void }> = (props) => {
  const { t } = useI18n();
  return (
    <div
      class="relative flex aspect-video w-full flex-col items-center justify-center gap-3 bg-neutral-900 text-center text-white"
      data-testid="video-offline"
      style={{
        "background-image": `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)), url("${props.poster}")`,
        "background-size": "cover",
        "background-position": "center",
      }}
    >
      <WifiOff class="h-9 w-9 text-white/80" />
      <p class="px-6 text-sm font-medium">{t(msg`The video needs a connection`)}</p>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-xl bg-white/15 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        data-testid="video-retry"
        onClick={() => props.onRetry()}
      >
        <RefreshCw class="h-4 w-4" /> {t(msg`Retry`)}
      </button>
    </div>
  );
};
